import "@/App.css";
import "@/lib/axiosGuard";
import { Turnstile } from "@/components/Turnstile";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { PageMeta } from "@/components/PageMeta";
import GpsOverlay from "@/components/GpsOverlay";
import { AuthProvider, useAuth } from "@/business/AuthContext";
import { ConsumerAuthProvider, ConsumerAuthCallback } from "@/consumer/ConsumerAuth";
import ConsentGate from "@/components/ConsentGate";
import { Toaster } from "sonner";
import InstallPrompt from "@/components/InstallPrompt";
import Onboarding from "@/components/Onboarding";

// 🔐 CANONICAL HOST LATCH — prod login-loop fix (RCA d2dfad4a): on www.airpog.com the session
// cookie set by airpog.com/api is treated as third-party and dropped → auth loop. Snap www → apex
// BEFORE the app boots, preserving path/search/#session_id so the OAuth return-trip survives the hop.
(() => {
  try {
    const apex = new URL(process.env.REACT_APP_BACKEND_URL).hostname;
    if (window.location.hostname === `www.${apex}`) {
      window.location.replace(`${window.location.protocol}//${apex}${window.location.pathname}${window.location.search}${window.location.hash}`);
    }
  } catch {}
})();

// Code-split non-driver routes so the consumer map loads lean & fast.
const BusinessAuth = lazy(() => import("@/business/BusinessAuth"));
const BusinessDashboard = lazy(() => import("@/business/BusinessDashboard"));
const RecruitCard = lazy(() => import("@/business/RecruitCard"));
const HotSpots = lazy(() => import("@/hotspots/HotSpots"));
const LegalPage = lazy(() => import("@/legal/LegalPage"));
const VerifyRedeem = lazy(() => import("@/redeem/VerifyRedeem"));
const ScanVisit = lazy(() => import("@/redeem/ScanVisit"));
const AdminStats = lazy(() => import("@/admin/AdminStats"));
const ShowcasePage = lazy(() => import("@/pages/ShowcasePage"));
const TravelPage = lazy(() => import("@/pages/TravelPage"));
const PitchSheet = lazy(() => import("@/admin/PitchSheet"));
const BlogList = lazy(() => import("@/blog/BlogList"));
const BlogPost = lazy(() => import("@/blog/BlogPost"));
const CoveragePage = lazy(() => import("@/pages/Coverage"));
const LingoPage = lazy(() => import("@/lingo/LingoPage"));
const PogopediaPage = lazy(() => import("@/pages/Pogopedia"));
const PogAlongPage = lazy(() => import("@/pogalong/PogAlongPage"));
const PogCrew = lazy(() => import("@/crew/PogCrew"));
const TravelScout = lazy(() => import("@/lts/TravelScout"));
const SportsHub = lazy(() => import("@/sports/SportsHub"));
const SuggestionBox = lazy(() => import("@/suggestions/SuggestionBox"));
const MyPings = lazy(() => import("@/pings/MyPings"));
const RunBoard = lazy(() => import("@/runs/RunBoard"));
const AirPogsPage = lazy(() => import("@/collect/AirPogsPage"));
const UnlockPage = lazy(() => import("@/collect/UnlockPage"));
const GramPage = lazy(() => import("@/collect/GramPage"));
const SlammerGame = lazy(() => import("@/games/SlammerGame"));
const SlotMachine = lazy(() => import("@/games/SlotMachine"));
const PogJack = lazy(() => import("@/games/PogJack"));
const PogSpades = lazy(() => import("@/games/PogSpades"));
const PogItaire = lazy(() => import("@/games/PogItaire"));
const PogketWallet = lazy(() => import("@/pages/PogketWallet"));
const WalletShare = lazy(() => import("@/pages/WalletShare"));
const LanePortal = lazy(() => import("@/pages/LanePortal"));
const GamesHub = lazy(() => import("@/games/GamesHub"));
const FleetShowcase = lazy(() => import("@/business/FleetShowcase"));
const SkyCalendar = lazy(() => import("@/components/SkyCalendar"));
const BalloonPublicPage = lazy(() => import("@/business/BalloonPublicPage"));
const BattlePage = lazy(() => import("@/battle/BattlePage"));
const MonkeyPog = lazy(() => import("@/games/MonkeyPog"));
const PogoGame = lazy(() => import("@/games/PogoGame"));
const CheckerPog = lazy(() => import("@/games/CheckerPog"));
const PogWalk = lazy(() => import("@/games/PogWalk"));
const PogAthon = lazy(() => import("@/games/PogAthon"));
const PogWord = lazy(() => import("@/games/PogWord"));
const PogCross = lazy(() => import("@/games/PogCross"));
const JobsRadar = lazy(() => import("@/jobs/JobsRadar"));
const TrophyCabinet = lazy(() => import("@/games/TrophyCabinet"));
const ChampWall = lazy(() => import("@/games/ChampWall"));
const ReelsFeed = lazy(() => import("@/reels/ReelsFeed"));
const FoundersWall = lazy(() => import("@/pages/FoundersWall"));
const CorporatePage = lazy(() => import("@/pages/CorporatePage"));
const StoryPage = lazy(() => import("@/pages/StoryPage"));
const PogVision = lazy(() => import("@/pages/PogVision"));
const Mailroom = lazy(() => import("@/pages/Mailroom"));
const FlicsFeed = lazy(() => import("@/flics/FlicsFeed"));
const PogLiveModule = lazy(() => import("@/reels/PogLive").then((m) => ({ default: m.GoLive })));
const LiveViewerPage = lazy(() => import("@/reels/PogLive").then((m) => ({ default: m.LiveViewer })));
const SellerProfile = lazy(() => import("@/reels/SellerProfile"));
const MerchantAppPage = lazy(() => import("@/consumer/MerchantApp"));
const MyPogAppsPage = lazy(() => import("@/consumer/MyPogApps"));
const PointsPage = lazy(() => import("@/points/PointsPage"));
const NetPage = lazy(() => import("@/net/NetPage"));
const HqPage = lazy(() => import("@/hq/HqPage"));
const AskPogPage = lazy(() => import("@/pog/AskPogPage"));
const PogzRusPage = lazy(() => import("@/pogzrus/PogzRusPage"));
const PogKingdomPage = lazy(() => import("@/story/PogKingdomPage"));
const InboxPage = lazy(() => import("@/inbox/InboxPage"));
const ScanShortcut = lazy(() => import("@/business/ScanShortcut"));
const TradePage = lazy(() => import("@/collect/TradePage"));
const CardShopsPage = lazy(() => import("@/outreach/CardShopsPage"));
const WantedBoard = lazy(() => import("@/collect/WantedBoard"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const ComicsPage = lazy(() => import("@/pages/ComicsPage"));
const TicketsPage = lazy(() => import("@/tickets/TicketsPage"));
const PogMarketPage = lazy(() => import("@/market/PogMarketPage"));
const ValidateRewardPage = lazy(() => import("@/market/ValidateRewardPage"));
const PogHeavenPage = lazy(() => import("@/heaven/PogHeavenPage"));
const MerchantPitchPage = lazy(() => import("@/pages/MerchantPitchPage"));

function FullPageLoader() {
  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center gap-4">
      <img src="/pogzilla.png" alt="POGZILLA" className="w-24 h-24 object-contain animate-bounce drop-shadow-[0_0_24px_rgba(52,211,153,0.5)]" />
      <div className="flex items-center gap-2 text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
        <span className="font-semibold text-sm">Just POG it…</span>
      </div>
    </div>
  );
}

function BusinessGate() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  return user ? <BusinessDashboard /> : <BusinessAuth />;
}

function ConsumerApp() {
  return (
    <ConsumerAuthProvider>
      <GpsOverlay />
      <Onboarding />
    </ConsumerAuthProvider>
  );
}

// 💛 FRONT DOOR — first-ever visit lands on the map (the hook); every open after that lands on the wallet.
// Redirect only applies when the page load itself started at "/" — in-app returns to the map always work.
const bootedAtHome = typeof window !== "undefined" && window.location.pathname === "/";
let bootRouted = false;
function HomeGate() {
  const navigate = useNavigate();
  const [showMap, setShowMap] = useState(false);
  useEffect(() => {
    const first = !localStorage.getItem("airpog_seen");
    localStorage.setItem("airpog_seen", "1");
    if (!bootRouted && bootedAtHome && !first) {
      bootRouted = true;
      navigate("/wallet", { replace: true });
      return;
    }
    bootRouted = true;
    setShowMap(true);
  }, [navigate]);
  if (!showMap) return <FullPageLoader />;
  return <><PageMeta path="/" /><ConsumerApp /></>;
}

function AppRouter() {
  const location = useLocation();
  // Google Analytics: report every in-app navigation as a page view (SPA).
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", { page_path: location.pathname + location.search });
    }
  }, [location.pathname, location.search]);
  // Emergent Google auth returns to {origin}/#session_id=... — process it before normal routing.
  if (location.hash?.includes("session_id=")) {
    return <ConsumerAuthCallback />;
  }
  // pogzrus.com — the collectibles/factory home. Land visitors on the POGZ R US storefront.
  if (typeof window !== "undefined" && /(^|\.)pogzrus\.com$/i.test(window.location.hostname) && location.pathname === "/") {
    return <Navigate to="/pogzrus" replace />;
  }
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Turnstile />
      <Routes>
        <Route path="/" element={<HomeGate />} />
        <Route path="/wallet" element={<ConsumerAuthProvider><PageMeta title="POG'KET WALLET ♥" description="Your personal launcher — saved merchants, deals and apps, one tap away on every device." path="/wallet" /><PogketWallet /></ConsumerAuthProvider>} />
        <Route path="/w/:shareId" element={<ConsumerAuthProvider><PageMeta title="A POG'R's POG'KET 💛" description="One app for all apps — browse this POG'R's wallet and copy the setup in one tap." path="/wallet" /><WalletShare /></ConsumerAuthProvider>} />
        <Route path="/lanes" element={<><PageMeta title="Billboard Lanes 💼" description="Own the ad strip beside your icon in every POG'R wallet — $99/mo or $999/yr." path="/lanes" /><LanePortal /></>} />
        <Route path="/d/:bizId" element={<ConsumerApp />} />
        <Route path="/pog/:pogId" element={<ConsumerApp />} />
        <Route path="/recruit" element={<><PageMeta title="Recruit a Business" description="Invite a local business to AIR.POG and earn POG BUCKS." path="/recruit" /><RecruitCard /></>} />
        <Route path="/terms" element={<><PageMeta title="Terms of Service" path="/terms" /><LegalPage doc="terms" /></>} />
        <Route path="/privacy" element={<><PageMeta title="Privacy Policy" path="/privacy" /><LegalPage doc="privacy" /></>} />
        <Route path="/verify/:token" element={<VerifyRedeem />} />
        <Route path="/scan" element={<ScanVisit />} />
        <Route path="/scan/:token" element={<ScanVisit />} />
        <Route path="/admin" element={<AdminStats />} />
        <Route path="/admin/pitch" element={<PitchSheet />} />
        <Route path="/blog" element={<ConsumerAuthProvider><PageMeta title="POG BLOG" description="Stories, tips & community posts from POG'Rs and local businesses on AIR.POG." path="/blog" /><BlogList /></ConsumerAuthProvider>} />
        <Route path="/blog/:slug" element={<ConsumerAuthProvider><BlogPost /></ConsumerAuthProvider>} />
        <Route path="/coverage" element={<CoveragePage />} />
        <Route path="/lingo" element={<ConsumerAuthProvider><PageMeta title="POG LINGO" description="Learn the official AIR.POG lingo — POGNIFICENT, POG'D IT!, POG AWAY and more." path="/lingo" /><LingoPage /></ConsumerAuthProvider>} />
        <Route path="/pogopedia" element={<PogopediaPage />} />
        <Route path="/pogalong" element={<ConsumerAuthProvider><PogAlongPage /></ConsumerAuthProvider>} />
        <Route path="/crew" element={<ConsumerAuthProvider><PageMeta title="MY POG CREW" description="POG together — friend fellow POG'RS and build your crew on AIR.POG." path="/crew" /><PogCrew /></ConsumerAuthProvider>} />
        <Route path="/lts" element={<ConsumerAuthProvider><PageMeta title="LOCAL TRAVEL SCOUT" description="Your town, scouted — local favorites, festivals, attractions, dining and more, voted on by local POG'RS." path="/lts" /><TravelScout /></ConsumerAuthProvider>} />
        <Route path="/sports" element={<ConsumerAuthProvider><PageMeta title="AIRPOG SPORTS" description="PRO, COLLEGE and NIL sports deals near you — game-day specials, campus-town steals and hometown athlete deals, POG'D by the fans." path="/sports" /><SportsHub /></ConsumerAuthProvider>} />
        <Route path="/suggestions" element={<ConsumerAuthProvider><PageMeta title="POG SUGGESTION BOX" description="Tell AIR.POG what to build next — drop ideas, vote up the best, earn POG BUCKS." path="/suggestions" /><SuggestionBox /></ConsumerAuthProvider>} />
        <Route path="/pings" element={<ConsumerAuthProvider><PageMeta title="MY POG PINGS" description="Your delivery and meet-up requests — Phase 1 of the POG RUN NETWORK." path="/pings" /><MyPings /></ConsumerAuthProvider>} />
        <Route path="/runs" element={<ConsumerAuthProvider><PageMeta title="POG RUN" description="Claim local delivery runs and keep 75% of every fee plus all tips — AIR.POG cares about its runners." path="/runs" /><RunBoard /></ConsumerAuthProvider>} />
        <Route path="/collect" element={<ConsumerAuthProvider><PageMeta title="POGZ Collection" description="Collect digital AIR.POGZ — crack packs, win rare cards, and complete your collection." path="/collect" /><AirPogsPage /></ConsumerAuthProvider>} />
        <Route path="/trade/:code" element={<ConsumerAuthProvider><TradePage /></ConsumerAuthProvider>} />
        <Route path="/unlock/:code" element={<ConsumerAuthProvider><UnlockPage /></ConsumerAuthProvider>} />
        <Route path="/gram/:code" element={<ConsumerAuthProvider><GramPage /></ConsumerAuthProvider>} />
        <Route path="/wanted" element={<ConsumerAuthProvider><WantedBoard /></ConsumerAuthProvider>} />
        <Route path="/cardshops" element={<CardShopsPage />} />
        <Route path="/hotspots" element={<><PageMeta title="HOT SPOTS" description="The most-tapped merchants on the POG STRIP — see where POG'RS are heading this week." path="/hotspots" /><HotSpots /></>} />
        <Route path="/showcase" element={<><PageMeta title="AIR.POG SHOWCASE" description="AIR.POG — live local deals on a living, playable map. The one-tap showcase card." path="/showcase" /><ShowcasePage /></>} />
        <Route path="/travel" element={<ConsumerAuthProvider><PageMeta title="AIR.POG TRAVEL" description="Catch a POG FLIGHT — book hotels, flights, vacations and rides through AIR.POG's travel partners." path="/travel" /><TravelPage /></ConsumerAuthProvider>} />
        <Route path="/inbox" element={<ConsumerAuthProvider><PageMeta title="Inbox" path="/inbox" /><InboxPage /></ConsumerAuthProvider>} />
        <Route path="/battle" element={<ConsumerAuthProvider><PageMeta title="POG BATTLE" description="Battle your POGZ — Heroes vs Villains! Pick your card and fight for your faction." path="/battle" /><BattlePage /></ConsumerAuthProvider>} />
        <Route path="/monkey" element={<ConsumerAuthProvider><PageMeta title="Monkey POGZ — Memory Match" description="Flip the caps, match the POGZ, beat the clock! Nail a Monkey POG first-try to win it." path="/monkey" /><MonkeyPog /></ConsumerAuthProvider>} />
        <Route path="/pogo" element={<ConsumerAuthProvider><PogoGame /></ConsumerAuthProvider>} />
        <Route path="/slammer" element={<ConsumerAuthProvider><SlammerGame /></ConsumerAuthProvider>} />
        <Route path="/slots" element={<ConsumerAuthProvider><SlotMachine /></ConsumerAuthProvider>} />
        <Route path="/pogjack" element={<ConsumerAuthProvider><PageMeta title="POG'JACK" description="Blackjack vs the POG dealer — bet your POG BUCKS, POG'JACK pays 2.5×." path="/pogjack" /><PogJack /></ConsumerAuthProvider>} />
        <Route path="/pogspades" element={<ConsumerAuthProvider><PageMeta title="POG SPADES" description="Cutthroat spades vs 3 POG bots — bid it, make it, bank POG BUCKS." path="/pogspades" /><PogSpades /></ConsumerAuthProvider>} />
        <Route path="/pogitaire" element={<ConsumerAuthProvider><PageMeta title="POGITAIRE 🃏" description="Klondike solitaire, POG'IFIED — clear the board, bank POG BUCKS." path="/pogitaire" /><PogItaire /></ConsumerAuthProvider>} />
        <Route path="/games" element={<ConsumerAuthProvider><GamesHub /></ConsumerAuthProvider>} />
        <Route path="/pogwalk" element={<ConsumerAuthProvider><PogWalk /></ConsumerAuthProvider>} />
        <Route path="/pogathon" element={<ConsumerAuthProvider><PogAthon /></ConsumerAuthProvider>} />
        <Route path="/pogword" element={<ConsumerAuthProvider><PogWord /></ConsumerAuthProvider>} />
        <Route path="/pogcross" element={<ConsumerAuthProvider><PogCross /></ConsumerAuthProvider>} />
        <Route path="/jobs" element={<JobsRadar />} />
        <Route path="/fleet" element={<FleetShowcase />} />
        <Route path="/sky-calendar" element={<SkyCalendar />} />
        <Route path="/balloon/:businessId" element={<BalloonPublicPage />} />
        <Route path="/checker" element={<ConsumerAuthProvider><PageMeta title="CHECKER POG" description="Classic checkers with a POG twist — king your caps and clear the board." path="/checker" /><CheckerPog /></ConsumerAuthProvider>} />
        <Route path="/points" element={<ConsumerAuthProvider><PageMeta title="POG BUCKS & Rewards" description="Earn POG BUCKS at local businesses, climb the leaderboard, and cash in rewards." path="/points" /><PointsPage /></ConsumerAuthProvider>} />
        <Route path="/net" element={<ConsumerAuthProvider><PageMeta title="AIRPOG NET" path="/net" /><NetPage /></ConsumerAuthProvider>} />
        <Route path="/hq" element={<ConsumerAuthProvider><PageMeta title="POG HQ" path="/hq" /><HqPage /></ConsumerAuthProvider>} />
        <Route path="/trophies" element={<ConsumerAuthProvider><TrophyCabinet /></ConsumerAuthProvider>} />
        <Route path="/champs" element={<ChampWall />} />
        <Route path="/reels" element={<ConsumerAuthProvider><ReelsFeed /></ConsumerAuthProvider>} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/comics" element={<ComicsPage />} />
        <Route path="/tickets" element={<ConsumerAuthProvider><TicketsPage /></ConsumerAuthProvider>} />
        <Route path="/market" element={<ConsumerAuthProvider><PageMeta title="POG MARKET 🛒" description="Spend POG BUCKS on real rewards at real merchants." path="/market" /><PogMarketPage /></ConsumerAuthProvider>} />
        <Route path="/validate/:token" element={<ValidateRewardPage />} />
        <Route path="/heaven" element={<><PageMeta title="POG'HEAVEN ☁️" description="The celestial realm — enshrined legends, ascended deals, and the rarest POGZ relics." path="/heaven" /><PogHeavenPage /></>} />
        <Route path="/pitch" element={<MerchantPitchPage />} />
        <Route path="/flics" element={<ConsumerAuthProvider><FlicsFeed /></ConsumerAuthProvider>} />
        <Route path="/golive" element={<ConsumerAuthProvider><PogLiveModule /></ConsumerAuthProvider>} />
        <Route path="/live/:liveId" element={<ConsumerAuthProvider><LiveViewerPage /></ConsumerAuthProvider>} />
        <Route path="/founders" element={<><PageMeta title="THE FOUNDING 100 🏆 — AIR.POG" description="The first SUPER POG merchants — founding rate locked for life, names etched forever." path="/founders" /><FoundersWall /></>} />
        <Route path="/corporate" element={<><PageMeta title="CORPORATE POG 🏢 — AIR.POG for chains" description="FLEET MATH: your whole fleet on the live deals map for less than one month of Google Ads. No auctions. No cannibalization." path="/corporate" /><CorporatePage /></>} />
        <Route path="/story" element={<><PageMeta title="THE 30-YEAR STORY 📖 — Why AIR.POG exists" description="The origin of the live deals map: 30 years of dreaming, built for the little giants the big maps ignore." path="/story" /><StoryPage /></>} />
        <Route path="/vision" element={<><PageMeta title="POG VISION 👁️ — AR deals over the real street" description="Point your phone down the street and watch live deals float on reality. AR by AIR.POG." path="/vision" /><PogVision /></>} />
        <Route path="/mailroom" element={<Mailroom />} />
        <Route path="/seller/:sellerId" element={<ConsumerAuthProvider><SellerProfile /></ConsumerAuthProvider>} />
        <Route path="/pogapp/:bizId" element={<ConsumerAuthProvider><MerchantAppPage /></ConsumerAuthProvider>} />
        <Route path="/myapps" element={<ConsumerAuthProvider><MyPogAppsPage /></ConsumerAuthProvider>} />
        <Route path="/ask-pog" element={<ConsumerAuthProvider><PageMeta title="Ask AIR.POG" description="Ask POG BOT anything — deals, directions, and POG wisdom." path="/ask-pog" /><AskPogPage /></ConsumerAuthProvider>} />
        <Route path="/pogzrus" element={<ConsumerAuthProvider><PageMeta title="POGZ R US" description="The POGZ factory & storefront — mint counts, rarities, and the full catalog." path="/pogzrus" /><PogzRusPage /></ConsumerAuthProvider>} />
        <Route path="/legend" element={<><PageMeta title="The Legend of POG Kingdom" path="/legend" /><PogKingdomPage /></>} />
        <Route
          path="/business"
          element={
            <AuthProvider>
              <PageMeta title="For Business — Claim Your Listing" description="Claim your free AIR.POG listing, post live deals, and light up THE POG STRIP." path="/business" />
              <BusinessGate />
            </AuthProvider>
          }
        />
        <Route
          path="/business/scan"
          element={
            <AuthProvider>
              <ScanShortcut />
            </AuthProvider>
          }
        />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
          <ConsentGate />
          <Toaster theme="dark" position="top-center" richColors closeButton style={{ zIndex: 99999 }} />
        </BrowserRouter>
        <InstallPrompt />
      </div>
    </HelmetProvider>
  );
}

export default App;
